(() => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const closeMenu = (menu) => {
    if (menu instanceof HTMLDetailsElement) {
      menu.removeAttribute('open');
      menu.querySelector('summary')?.focus();
    }
  };

  const announcement = document.querySelector('[data-rhisa-announcement]');
  if (announcement instanceof HTMLElement) {
    let announcementFrame = 0;
    const updateAnnouncement = () => {
      announcement.classList.toggle('rhisa-announcement-bar--hidden', window.scrollY > 1);
      announcementFrame = 0;
    };

    updateAnnouncement();
    window.addEventListener(
      'scroll',
      () => {
        if (announcementFrame) return;
        announcementFrame = window.requestAnimationFrame(updateAnnouncement);
      },
      { passive: true }
    );
  }

  const header = document.querySelector('[data-rhisa-header]');
  if (header instanceof HTMLElement) {
    const scrollThreshold = 20;
    let lastScrollY = Math.max(window.scrollY, 0);
    let directionStartY = lastScrollY;
    let direction = 0;
    let headerFrame = 0;

    const updateHeader = () => {
      const currentScrollY = Math.max(window.scrollY, 0);
      const nextDirection = Math.sign(currentScrollY - lastScrollY);
      const isAtTop = currentScrollY <= 1;

      header.classList.toggle('rhisa-header--scrolled', !isAtTop);

      if (isAtTop) {
        header.classList.remove('rhisa-header--hidden');
        direction = 0;
        directionStartY = 0;
      } else if (nextDirection && nextDirection !== direction) {
        direction = nextDirection;
        directionStartY = lastScrollY;
      } else if (direction > 0 && currentScrollY - directionStartY >= scrollThreshold) {
        header.classList.add('rhisa-header--hidden');
        directionStartY = currentScrollY;
      } else if (direction < 0 && directionStartY - currentScrollY >= scrollThreshold) {
        header.classList.remove('rhisa-header--hidden');
        directionStartY = currentScrollY;
      }

      if (header.contains(document.activeElement)) header.classList.remove('rhisa-header--hidden');
      lastScrollY = currentScrollY;
      headerFrame = 0;
    };

    updateHeader();
    window.addEventListener(
      'scroll',
      () => {
        if (headerFrame) return;
        headerFrame = window.requestAnimationFrame(updateHeader);
      },
      { passive: true }
    );
    header.addEventListener('focusin', () => header.classList.remove('rhisa-header--hidden'));
  }

  document.querySelectorAll('[data-rhisa-menu]').forEach((menu) => {
    menu.addEventListener('toggle', () => {
      if (!menu.open) return;

      window.setTimeout(() => {
        menu.querySelector('[data-rhisa-close-menu]')?.focus();
      }, 0);
    });
  });

  const megaMenus = Array.from(document.querySelectorAll('[data-rhisa-mega-menu]'));
  megaMenus.forEach((menu) => {
    let closeTimer = 0;
    const openMenu = () => {
      window.clearTimeout(closeTimer);
      megaMenus.forEach((otherMenu) => {
        if (otherMenu !== menu) otherMenu.removeAttribute('open');
      });
      menu.setAttribute('open', '');
    };
    const scheduleClose = () => {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => menu.removeAttribute('open'), 140);
    };

    menu.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch') return;
      openMenu();
    });
    menu.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch') return;
      scheduleClose();
    });
    menu.addEventListener('focusin', openMenu);
    menu.addEventListener('focusout', (event) => {
      if (!menu.contains(event.relatedTarget)) scheduleClose();
    });
    menu.addEventListener('toggle', () => {
      if (!menu.open) return;
      megaMenus.forEach((otherMenu) => {
        if (otherMenu !== menu) otherMenu.removeAttribute('open');
      });
    });
  });

  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-rhisa-close-menu]');
    if (closeButton) {
      closeMenu(closeButton.closest('[data-rhisa-menu]'));
      return;
    }

    const backdrop = event.target.closest('[data-rhisa-menu-backdrop]');
    if (backdrop && event.target === backdrop) {
      closeMenu(backdrop.closest('[data-rhisa-menu]'));
      return;
    }

    megaMenus.forEach((menu) => {
      if (menu.open && !menu.contains(event.target)) menu.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    const openMenu = document.querySelector('[data-rhisa-menu][open], [data-rhisa-mega-menu][open]');
    if (!openMenu) return;

    if (event.key === 'Escape') {
      closeMenu(openMenu);
      return;
    }

    if (event.key !== 'Tab' || openMenu.matches('[data-rhisa-mega-menu]')) return;

    const focusScope = openMenu.querySelector('[data-rhisa-menu-backdrop]') || openMenu;
    const focusableElements = Array.from(focusScope.querySelectorAll(focusableSelector)).filter(
      (element) => !element.hasAttribute('disabled') && element.getClientRects().length
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (!firstElement || !lastElement) {
      event.preventDefault();
      return;
    }

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  });

  document.querySelectorAll('[data-rhisa-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[role="region"]');
    const previous = carousel.querySelector('[data-rhisa-carousel-previous]');
    const next = carousel.querySelector('[data-rhisa-carousel-next]');

    if (!(track instanceof HTMLElement)) return;

    const scrollByPage = (direction) => {
      const distance = Math.max(track.clientWidth * 0.82, 240);
      track.scrollBy({ left: direction * distance, behavior: 'smooth' });
    };

    previous?.addEventListener('click', () => scrollByPage(-1));
    next?.addEventListener('click', () => scrollByPage(1));
  });

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const compactHeroViewport = window.matchMedia('(max-width: 749px)');
  const keepHeroPosterStatic = () => reducedMotion.matches || compactHeroViewport.matches || navigator.connection?.saveData === true;
  const playHeroVideo = (video) => {
    video.play().then(() => video.classList.add('is-ready')).catch(() => {});
  };
  const loadHeroVideo = (video) => {
    if (video.dataset.videoLoaded === 'true' || keepHeroPosterStatic()) return;

    video.querySelectorAll('source[data-src]').forEach((source) => {
      if (!(source instanceof HTMLSourceElement) || !source.dataset.src) return;
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.dataset.videoLoaded = 'true';
    video.load();
    playHeroVideo(video);
  };

  const syncHeroVideoMotion = () => {
    document.querySelectorAll('[data-rhisa-hero-video]').forEach((video) => {
      if (!(video instanceof HTMLVideoElement)) return;
      if (keepHeroPosterStatic()) {
        video.pause();
        video.classList.remove('is-ready');
      } else if (video.dataset.videoLoaded === 'true') {
        playHeroVideo(video);
      } else {
        const scheduleLoad = () => loadHeroVideo(video);
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(scheduleLoad, { timeout: 1500 });
        } else {
          window.setTimeout(scheduleLoad, 800);
        }
      }
    });
  };
  syncHeroVideoMotion();
  reducedMotion.addEventListener?.('change', syncHeroVideoMotion);
  compactHeroViewport.addEventListener?.('change', syncHeroVideoMotion);
})();
